// services/teamService.ts
import { supabase } from './supabaseClient';
import { TeamEmailService } from './teamEmailService';
import {
  TeamRequest,
  TeamApplication,
  Team,
  TeamSettings,
  CreateTeamRequestData,
  CreateApplicationData,
  ReviewApplicationData,
  FormTeamData
} from '../types/team';

export class TeamService {
  // Team Settings
  static async getTeamSettings(): Promise<TeamSettings[]> {
    const { data, error } = await supabase
      .from('team_settings')
      .select('*')
      .order('setting_name');

    if (error) throw error;
    return data || [];
  }

  static async updateTeamSetting(id: string, value: number): Promise<void> {
    const { error } = await supabase
      .from('team_settings')
      .update({
        setting_value: value,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  }

  // Team Requests
  static async getTeamRequests(filters?: {
    status?: string;
    publisher_id?: string;
    search?: string;
    exclude_user_id?: string; // New filter to exclude user's own teams
  }): Promise<TeamRequest[]> {
    let query = supabase
      .from('team_requests')
      .select(`
        *,
        publisher:users!team_requests_publisher_id_fkey(
          id, username, display_name, avatar_url
        ),
        applications:team_applications(count)
      `)
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.publisher_id) {
      query = query.eq('publisher_id', filters.publisher_id);
    }

    // Exclude teams created by the user (for Available Teams tab)
    if (filters?.exclude_user_id) {
      query = query.neq('publisher_id', filters.exclude_user_id);
    }

    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%, project_introduction.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Transform data to include applications_count
    return (data || []).map(request => ({
      ...request,
      applications_count: request.applications?.[0]?.count || 0
    }));
  }

  static async getTeamRequest(id: string): Promise<TeamRequest | null> {
    const { data, error } = await supabase
      .from('team_requests')
      .select(`
        *,
        publisher:users!team_requests_publisher_id_fkey(
          id, username, display_name, avatar_url
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  static async createTeamRequest(data: CreateTeamRequestData): Promise<string> {
    const { data: result, error } = await supabase
      .rpc('create_team_request', {
        p_title: data.title,
        p_project_introduction: data.project_introduction,
        p_team_size: data.team_size,
        p_min_time_commitment: data.min_time_commitment,
        p_skills_required: data.skills_required,
        p_additional_requirements: data.additional_requirements || null,
        p_deadline: data.deadline
      });

    if (error) throw error;
    return result;
  }

  static async updateTeamRequestStatus(id: string, status: string): Promise<void> {
    const { error } = await supabase
      .from('team_requests')
      .update({
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  }

  static async calculateTeamCost(teamSize: number): Promise<number> {
    const { data, error } = await supabase
      .rpc('calculate_team_cost', { team_size: teamSize });

    if (error) throw error;
    return data;
  }

  // Team Applications
  static async getApplicationsForRequest(requestId: string): Promise<TeamApplication[]> {
    const { data, error } = await supabase
      .from('team_applications')
      .select(`
        *,
        applicant:users!team_applications_applicant_id_fkey(
          id, username, display_name, avatar_url
        )
      `)
      .eq('team_request_id', requestId)
      .order('applied_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async getMyApplications(userId: string): Promise<TeamApplication[]> {
    console.log('getMyApplications called for user:', userId);

    const { data, error } = await supabase
      .from('team_applications')
      .select(`
        *,
        team_request:team_requests!team_applications_team_request_id_fkey(
          id, title, publisher_id, status, deadline, project_introduction, team_size
        )
      `)
      .eq('applicant_id', userId)
      .order('applied_at', { ascending: false });

    if (error) {
      console.error('Error fetching my applications:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      throw error;
    }

    console.log('My applications data:', data);
    console.log('Number of applications found:', data?.length || 0);

    // Add detailed logging for each application
    if (data && data.length > 0) {
      data.forEach((app, index) => {
        console.log(`Application ${index + 1}:`, {
          id: app.id,
          status: app.status,
          team_request_id: app.team_request_id,
          team_title: app.team_request?.title,
          applied_at: app.applied_at
        });
      });
    }

    return data || [];
  }

  // Helper method to check team capacity
  static async checkTeamCapacity(teamRequestId: string): Promise<{ totalSlots: number; acceptedCount: number; availableSlots: number; teamSize: number }> {
    // Get team request details
    const { data: teamRequest, error: trError } = await supabase
      .from('team_requests')
      .select('team_size')
      .eq('id', teamRequestId)
      .single();

    if (trError) throw trError;
    if (!teamRequest) throw new Error('Team request not found');

    // Get count of accepted applications
    const { data: acceptedApps, error: appsError } = await supabase
      .from('team_applications')
      .select('id')
      .eq('team_request_id', teamRequestId)
      .eq('status', 'accepted');

    if (appsError) throw appsError;

    const acceptedCount = acceptedApps?.length || 0;
    const totalSlots = teamRequest.team_size - 1; // Subtract 1 for the owner
    const availableSlots = totalSlots - acceptedCount;

    return {
      totalSlots,
      acceptedCount,
      availableSlots,
      teamSize: teamRequest.team_size
    };
  }

  static async createApplication(data: CreateApplicationData): Promise<void> {
    // Get the current user first
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user) {
      throw new Error('User not authenticated');
    }

    console.log('Creating application with user ID:', userData.user.id);
    console.log('Application data:', data);

    // Check team capacity before allowing application
    const capacity = await this.checkTeamCapacity(data.team_request_id);
    if (capacity.availableSlots <= 0) {
      throw new Error(
        `This team has already reached its capacity. ` +
        `Team size is ${capacity.teamSize} (including owner), ` +
        `and ${capacity.acceptedCount} member(s) have already been accepted.`
      );
    }

    const { error } = await supabase
      .from('team_applications')
      .insert({
        team_request_id: data.team_request_id,
        applicant_id: userData.user.id,
        introduction: data.introduction,
        why_fit: data.why_fit,
        skills_highlight: data.skills_highlight,
        availability: data.availability
      });

    if (error) {
      console.error('Failed to create application:', error);
      throw new Error(`Failed to submit application: ${error.message} (Code: ${error.code})`);
    }
  }

  static async updateApplication(id: string, data: Partial<TeamApplication>): Promise<void> {
    const { error } = await supabase
      .from('team_applications')
      .update(data)
      .eq('id', id);

    if (error) throw error;
  }

  static async reviewApplication(data: ReviewApplicationData): Promise<void> {
    const { error } = await supabase
      .from('team_applications')
      .update({
        status: data.status,
        publisher_message: data.publisher_message,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', data.application_id);

    if (error) throw error;
  }

  static async withdrawApplication(id: string): Promise<void> {
    const { error } = await supabase
      .from('team_applications')
      .update({
        status: 'withdrawn'
      })
      .eq('id', id);

    if (error) throw error;
  }

  // Enhanced application management
  static async getApplicationsForMyRequests(publisherId: string): Promise<TeamApplication[]> {
    console.log('getApplicationsForMyRequests called for publisher:', publisherId);

    // Single optimized query using inner join and filters
    // The !inner hint ensures we only get applications where team_request matches our criteria
    const { data, error } = await supabase
      .from('team_applications')
      .select(`
        *,
        applicant:users!team_applications_applicant_id_fkey(
          id, username, display_name, avatar_url, email
        ),
        team_request:team_requests!inner(
          id, title, project_introduction, status, publisher_id, team_size
        )
      `)
      .eq('team_request.publisher_id', publisherId)
      .eq('team_request.status', 'active')
      .order('applied_at', { ascending: false });

    if (error) {
      console.error('Error fetching applications for my requests:', error);
      throw error;
    }

    console.log('Applications found for publisher requests:', data?.length || 0);

    // Group applications by team request for debugging
    const groupedApps = (data || []).reduce((acc, app) => {
      const requestId = app.team_request_id;
      if (!acc[requestId]) {
        acc[requestId] = { title: app.team_request?.title, applications: [] };
      }
      acc[requestId].applications.push(`${app.applicant?.username}(${app.status})`);
      return acc;
    }, {} as Record<string, any>);

    console.log('Grouped applications by team request:', Object.keys(groupedApps).length, 'requests');

    return data || [];
  }

  static async acceptApplication(applicationId: string, publisherMessage?: string): Promise<void> {
    // First, get the application details to know which team request this belongs to
    const { data: application, error: appError } = await supabase
      .from('team_applications')
      .select(`
        id,
        team_request_id,
        applicant_id,
        team_request:team_requests!team_applications_team_request_id_fkey(
          id, title, team_size, publisher_id, status
        )
      `)
      .eq('id', applicationId)
      .single();

    if (appError) throw appError;
    if (!application) throw new Error('Application not found');

    // Check team capacity before accepting
    const capacity = await this.checkTeamCapacity(application.team_request_id);
    if (capacity.availableSlots <= 0) {
      throw new Error(
        `Cannot accept this application - all required team members have been found. ` +
        `Team requires ${capacity.totalSlots} additional member(s) (plus owner = ${capacity.teamSize} total), ` +
        `and ${capacity.acceptedCount} member(s) have already been accepted.`
      );
    }

    // Accept the application
    const { error } = await supabase
      .from('team_applications')
      .update({
        status: 'accepted',
        publisher_message: publisherMessage,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', applicationId);

    if (error) throw error;

    // NOTE: Email sharing messages are now handled by database triggers
    // The handle_team_application_messages() trigger automatically creates
    // team_application_approved messages when applications are accepted
    // Keeping this code commented for reference:
    /*
    try {
      const teamRequest = application.team_request as any;
      await TeamEmailService.sendApplicationAcceptedMessages(
        application.team_request_id,
        application.applicant_id,
        teamRequest.publisher_id
      );
    } catch (emailError) {
      console.error('Error sending email sharing messages:', emailError);
    }
    */

    // Only check for auto-formation, don't automatically form
    // The publisher can manually form the team when ready
    console.log(`Application ${applicationId} accepted for team request ${application.team_request_id}`);
  }

  // Helper method to check if team should be auto-formed
  private static async checkAndAutoFormTeam(teamRequestId: string): Promise<void> {
    try {
      console.log('Checking auto-team formation for request:', teamRequestId);

      // Get team request details
      const { data: teamRequest, error: trError } = await supabase
        .from('team_requests')
        .select('id, title, team_size, publisher_id, status')
        .eq('id', teamRequestId)
        .single();

      if (trError || !teamRequest) {
        console.log('Team request not found:', trError);
        return;
      }

      console.log('Team request details:', teamRequest);

      // Skip if team already formed or request closed
      if (teamRequest.status !== 'active') {
        console.log('Team request not active, status:', teamRequest.status);
        return;
      }

      // Get accepted applications for this request
      const { data: acceptedApps, error: appsError } = await supabase
        .from('team_applications')
        .select('id, applicant_id')
        .eq('team_request_id', teamRequestId)
        .eq('status', 'accepted');

      if (appsError) {
        console.error('Error fetching accepted applications:', appsError);
        throw appsError;
      }

      // Check if we have enough accepted applications to form a team
      // Note: team_size includes the publisher, so we need (team_size - 1) accepted applications
      const requiredApplications = teamRequest.team_size - 1;
      const acceptedCount = acceptedApps?.length || 0;

      if (acceptedApps && acceptedCount >= requiredApplications) {
        // Auto-form the team
        const teamName = `${teamRequest.title} - Team`;
        const acceptedApplicationIds = acceptedApps.map(app => app.id);

        const teamId = await this.formTeam({
          request_id: teamRequestId,
          team_name: teamName,
          accepted_application_ids: acceptedApplicationIds
        });
      }
    } catch (error) {
    }
  }

  static async rejectApplication(applicationId: string, publisherMessage?: string): Promise<void> {
    const { error } = await supabase
      .from('team_applications')
      .update({
        status: 'rejected',
        publisher_message: publisherMessage,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', applicationId);

    if (error) throw error;

    // TODO: Add notification logic here
  }

  // Teams
  static async getTeams(filters?: {
    owner_id?: string;
    member_id?: string;
    status?: string;
  }): Promise<Team[]> {
    // If filtering by member_id, first get the team IDs where the user is a member
    let teamIdsFilter: string[] | undefined;
    if (filters?.member_id) {
      const { data: membershipData, error: membershipError } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('user_id', filters.member_id)
        .eq('is_active', true);

      if (membershipError) {
        console.error('Error fetching team memberships:', membershipError);
        throw membershipError;
      }

      teamIdsFilter = membershipData?.map(m => m.team_id) || [];

      // If user is not a member of any teams, return empty array
      if (teamIdsFilter.length === 0) {
        return [];
      }
    }

    let query = supabase
      .from('teams')
      .select(`
        *,
        owner:users!teams_owner_id_fkey(
          id, username, display_name, avatar_url
        ),
        team_members(
          id, user_id, role, joined_at, is_active,
          user:users!team_members_user_id_fkey(
            id, username, display_name, avatar_url
          )
        )
      `)
      .order('created_at', { ascending: false });

    if (filters?.owner_id) {
      query = query.eq('owner_id', filters.owner_id);
    }

    if (teamIdsFilter) {
      query = query.in('id', teamIdsFilter);
    }

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    // Transform the data to properly structure members and filter active members only
    return (data || []).map(team => {
      // Filter to only active team members
      const activeMembers = (team.team_members || []).filter((member: any) => member.is_active === true);

      return {
        ...team,
        members: activeMembers,
        members_count: activeMembers.length
      };
    });
  }

  static async getTeam(id: string): Promise<Team | null> {
    const { data, error } = await supabase
      .from('teams')
      .select(`
        *,
        owner:users!teams_owner_id_fkey(
          id, username, display_name, avatar_url
        ),
        team_members(
          id, user_id, role, joined_at, is_active,
          user:users!team_members_user_id_fkey(
            id, username, display_name, avatar_url
          )
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    // Filter to only active team members
    const activeMembers = (data.team_members || []).filter((member: any) => member.is_active === true);

    return {
      ...data,
      members: activeMembers,
      members_count: activeMembers.length
    };
  }

  static async formTeam(data: FormTeamData): Promise<string> {
    const { data: result, error } = await supabase
      .rpc('form_team', {
        p_request_id: data.request_id,
        p_team_name: data.team_name,
        p_accepted_application_ids: data.accepted_application_ids
      });

    if (error) throw error;

    // NOTE: Team formation messages are now handled by database triggers
    // The handle_team_formation_messages() trigger automatically creates
    // team_formed messages when team status is set to 'formed'
    // Keeping this code commented for reference:
    /*
    try {
      await TeamEmailService.sendTeamFormationMessages(result);
    } catch (emailError) {
      console.error('Error sending team formation messages:', emailError);
    }
    */

    return result;
  }

  static async updateTeamStatus(id: string, status: string): Promise<void> {
    const { error } = await supabase
      .from('teams')
      .update({
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  }

  // This method is replaced by the enhanced version below
  // Keeping this comment as a reference

  // Enhanced team management with notifications
  static async leaveTeam(teamId: string, userId: string): Promise<void> {
    try {
      // Get team and user info for notifications before leaving
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select(`
          id, name, owner_id,
          owner:users!teams_owner_id_fkey(username, display_name)
        `)
        .eq('id', teamId)
        .single();

      if (teamError) {
        console.error('Error fetching team data:', teamError);
        throw new Error('Could not fetch team information');
      }

      const { data: leavingUser, error: userError } = await supabase
        .from('users')
        .select('username, display_name')
        .eq('id', userId)
        .single();

      if (userError) {
        console.error('Error fetching user data:', userError);
        throw new Error('Could not fetch user information');
      }

      // Use a direct approach with a single query to update the team member status
      console.log('Attempting to leave team with ID:', teamId, 'for user:', userId);

      // First, get the specific team member record
      const { data: memberRecord, error: memberQueryError } = await supabase
        .from('team_members')
        .select('*')
        .eq('team_id', teamId)
        .eq('user_id', userId)
        .eq('is_active', true)
        .single();

      if (memberQueryError) {
        console.error('Error finding team membership:', memberQueryError);
        throw new Error('Could not find your active membership in this team');
      }

      if (!memberRecord) {
        throw new Error('You are not an active member of this team');
      }

      // Prevent owner from leaving their own team
      if (teamData.owner_id === userId) {
        throw new Error('As the team owner, you cannot leave the team. You must either transfer ownership or disband the team.');
      }

      console.log('Found team member record:', memberRecord);

      // Use a simple direct update approach
      console.log('Attempting to update team member status with direct update');

      // Try a direct update with team_id and user_id
      const { error: updateError } = await supabase
        .from('team_members')
        .update({ is_active: false })
        .eq('team_id', teamId)
        .eq('user_id', userId);

      if (updateError) {
        console.error('Direct update failed:', updateError);

        // Try with the member ID as a fallback
        console.log('Falling back to update with member ID:', memberRecord.id);

        const { error: fallbackError } = await supabase
          .from('team_members')
          .update({ is_active: false })
          .eq('id', memberRecord.id);

        if (fallbackError) {
          console.error('All update attempts failed:', fallbackError);
          throw new Error(`Failed to leave team: ${fallbackError.message}`);
        }
      }

      console.log('Successfully updated team member status');

      // Check if team falls below minimum requirements
      await this.checkTeamCapacityRequirements(teamId);

      // Send notifications to team owner and remaining members
      await this.sendTeamNotifications(teamId, {
        type: 'member_left',
        message: `${leavingUser.display_name || leavingUser.username} has left the team "${teamData.name}"`,
        excludeUserId: userId,
        teamName: teamData.name,
        memberName: leavingUser.display_name || leavingUser.username
      });

    } catch (error) {
      console.error('Error in leaveTeam:', error);
      throw error;
    }
  }

  static async removeTeamMember(teamId: string, userId: string, removedBy: string, reason?: string): Promise<void> {
    try {
      // Verify the remover is the team owner
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('owner_id, name')
        .eq('id', teamId)
        .single();

      if (teamError) throw teamError;
      if (team.owner_id !== removedBy) {
        throw new Error('Only team owner can remove members');
      }

      // Get user info for notifications
      const { data: removedUser, error: userError } = await supabase
        .from('users')
        .select('username, display_name')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // Remove team member - only update is_active since other columns might not exist
      const { error } = await supabase
        .from('team_members')
        .update({
          is_active: false
        })
        .eq('team_id', teamId)
        .eq('user_id', userId);

      if (error) throw error;

      // Check team capacity requirements
      await this.checkTeamCapacityRequirements(teamId);

      // Send notifications
      await this.sendTeamNotifications(teamId, {
        type: 'member_removed',
        message: `${removedUser.display_name || removedUser.username} has been removed from the team "${team.name}"${reason ? ` - Reason: ${reason}` : ''}`,
        excludeUserId: null, // Notify everyone including the removed member
        teamName: team.name,
        memberName: removedUser.display_name || removedUser.username,
        reason
      });

    } catch (error) {
      console.error('Error in removeTeamMember:', error);
      throw error;
    }
  }

  static async disbandTeam(teamId: string, disbandedBy: string, reason?: string): Promise<void> {
    try {
      // Verify the disbander is the team owner
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select(`
          id, name, owner_id,
          team_members!inner(
            user_id,
            user:users!team_members_user_id_fkey(username, display_name)
          )
        `)
        .eq('id', teamId)
        .single();

      if (teamError) throw teamError;
      if (team.owner_id !== disbandedBy) {
        throw new Error('Only team owner can disband the team');
      }

      // Update team status - only update columns that exist in the database
      const { error } = await supabase
        .from('teams')
        .update({
          status: 'disbanded',
          updated_at: new Date().toISOString()
        })
        .eq('id', teamId);

      if (error) throw error;

      // Deactivate all team members
      const { error: membersError } = await supabase
        .from('team_members')
        .update({
          is_active: false
        })
        .eq('team_id', teamId);

      if (membersError) throw membersError;

      // Send notifications to all team members
      await this.sendTeamNotifications(teamId, {
        type: 'team_disbanded',
        message: `The team "${team.name}" has been disbanded${reason ? ` - Reason: ${reason}` : ''}`,
        excludeUserId: null, // Notify everyone
        teamName: team.name,
        reason
      });

    } catch (error) {
      console.error('Error in disbandTeam:', error);
      throw error;
    }
  }

  // Check if team meets minimum capacity requirements
  static async checkTeamCapacityRequirements(teamId: string): Promise<void> {
    try {
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select(`
          id, name, team_request_id,
          team_request:team_requests!teams_team_request_id_fkey(team_size),
          team_members!inner(user_id)
        `)
        .eq('id', teamId)
        .eq('team_members.is_active', true)
        .single();

      if (teamError) throw teamError;

      const currentMemberCount = team.team_members?.length || 0;
      // Access team_size from the first element if it's an array
      // Use a direct approach with explicit type assertions
      let requiredSize = 1; // Default value

      if (team.team_request) {
        if (Array.isArray(team.team_request) && team.team_request.length > 0) {
          // Use direct type assertion with any
          requiredSize = (team.team_request[0] as any).team_size || 1;
        } else {
          // Use direct type assertion with any
          requiredSize = (team.team_request as any).team_size || 1;
        }
      }

      if (currentMemberCount < requiredSize) {
        // Team is below minimum capacity - could trigger recruitment
        await this.handleUnderCapacityTeam(teamId, currentMemberCount, requiredSize);
      }
    } catch (error) {
      console.error('Error checking team capacity:', error);
      // Don't throw - this is a background check
    }
  }

  // Handle teams that fall below minimum capacity
  static async handleUnderCapacityTeam(teamId: string, currentSize: number, requiredSize: number): Promise<void> {
    try {
      // Update team status to indicate it needs members - only update columns that exist
      const { error } = await supabase
        .from('teams')
        .update({
          status: 'recruiting',
          updated_at: new Date().toISOString()
        })
        .eq('id', teamId);

      if (error) throw error;

      // Notify team owner about the capacity issue
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('owner_id, name')
        .eq('id', teamId)
        .single();

      if (!teamError && team) {
        await this.sendNotificationToUser(team.owner_id, {
          type: 'team_under_capacity',
          title: 'Team Needs More Members',
          message: `Your team "${team.name}" now has ${currentSize} members but needs ${requiredSize}. Consider recruiting new members.`,
          teamId,
          teamName: team.name
        });
      }
    } catch (error) {
      console.error('Error handling under-capacity team:', error);
    }
  }

  // Send notifications to team members
  static async sendTeamNotifications(teamId: string, notification: {
    type: string;
    message: string;
    excludeUserId?: string | null;
    teamName: string;
    memberName?: string;
    reason?: string;
  }): Promise<void> {
    try {
      // Get all active team members
      const { data: members, error } = await supabase
        .from('team_members')
        .select('user_id')
        .eq('team_id', teamId)
        .eq('is_active', true);

      if (error) throw error;

      const notifications = members
        ?.filter(member => member.user_id !== notification.excludeUserId)
        .map(member => ({
          user_id: member.user_id,
          type: notification.type,
          title: this.getNotificationTitle(notification.type),
          message: notification.message,
          metadata: {
            team_id: teamId,
            team_name: notification.teamName,
            member_name: notification.memberName,
            reason: notification.reason
          },
          created_at: new Date().toISOString()
        })) || [];

      if (notifications.length > 0) {
        const { error: notifError } = await supabase
          .from('notifications')
          .insert(notifications);

        if (notifError) {
          console.error('Error sending team notifications:', notifError);
        }
      }
    } catch (error) {
      console.error('Error in sendTeamNotifications:', error);
    }
  }

  // Send notification to a specific user
  static async sendNotificationToUser(userId: string, notification: {
    type: string;
    title: string;
    message: string;
    teamId?: string;
    teamName?: string;
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type: notification.type,
          title: notification.title,
          message: notification.message,
          metadata: {
            team_id: notification.teamId,
            team_name: notification.teamName
          },
          created_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error sending user notification:', error);
      }
    } catch (error) {
      console.error('Error in sendNotificationToUser:', error);
    }
  }

  // Get notification title based on type
  static getNotificationTitle(type: string): string {
    switch (type) {
      case 'member_left':
        return 'Team Member Left';
      case 'member_removed':
        return 'Team Member Removed';
      case 'team_disbanded':
        return 'Team Disbanded';
      case 'team_under_capacity':
        return 'Team Needs Members';
      default:
        return 'Team Update';
    }
  }

  // Dashboard/Statistics
  static async getTeamStats(): Promise<{
    total_requests: number;
    active_requests: number;
    completed_teams: number;
    total_applications: number;
  }> {
    const [requestsRes, teamsRes, applicationsRes] = await Promise.all([
      supabase
        .from('team_requests')
        .select('id, status', { count: 'exact' }),
      supabase
        .from('teams')
        .select('id', { count: 'exact' }),
      supabase
        .from('team_applications')
        .select('id', { count: 'exact' })
    ]);

    if (requestsRes.error) throw requestsRes.error;
    if (teamsRes.error) throw teamsRes.error;
    if (applicationsRes.error) throw applicationsRes.error;

    const activeRequests = requestsRes.data?.filter(r => r.status === 'active').length || 0;

    return {
      total_requests: requestsRes.count || 0,
      active_requests: activeRequests,
      completed_teams: teamsRes.count || 0,
      total_applications: applicationsRes.count || 0
    };
  }

  // Get available teams for a user (excluding their own teams and teams they've applied to)
  static async getAvailableTeamsForUser(userId: string, filters?: {
    search?: string;
    skills?: string[];
  }): Promise<TeamRequest[]> {
    console.log('getAvailableTeamsForUser called for user:', userId);
    console.log('Filters:', filters);

    // First get team requests that are active and not created by the user
    let query = supabase
      .from('team_requests')
      .select(`
        *,
        publisher:users!team_requests_publisher_id_fkey(
          id, username, display_name, avatar_url
        ),
        applications:team_applications!left(count)
      `)
      .eq('status', 'active')
      .neq('publisher_id', userId)
      .order('created_at', { ascending: false });

    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%, project_introduction.ilike.%${filters.search}%`);
    }

    console.log('Fetching team requests...');
    const { data, error } = await query;

    if (error) {
      console.error('Error fetching team requests:', error);
      throw error;
    }

    console.log('Raw team requests found:', data?.length || 0);
    console.log('Team requests data:', data);

    if (!data || data.length === 0) {
      console.log('No team requests found, returning empty array');
      return [];
    }

    console.log('Fetching user applications to filter out applied teams...');
    // Get user's applications to filter out teams they've already applied to
    const { data: userApplications, error: appError } = await supabase
      .from('team_applications')
      .select('team_request_id, status')
      .eq('applicant_id', userId);

    if (appError) {
      console.error('Error fetching user applications:', appError);
      throw appError;
    }

    console.log('User applications found:', userApplications?.length || 0);
    console.log('User applications:', userApplications);

    const appliedTeamIds = new Set(userApplications?.map(app => app.team_request_id) || []);
    console.log('Applied team IDs:', Array.from(appliedTeamIds));

    // Filter out teams where user has already applied
    const availableTeams = data
      .filter(request => {
        const hasApplied = appliedTeamIds.has(request.id);
        console.log(`Team "${request.title}" (${request.id}): ${hasApplied ? 'already applied' : 'available'}`);
        return !hasApplied;
      })
      .map(request => ({
        ...request,
        applications_count: request.applications?.[0]?.count || 0,
        my_application: null,
        can_apply: true
      }));

    console.log('Available teams after filtering:', availableTeams.length);
    console.log('Available teams:', availableTeams);

    return availableTeams;
  }

  // Debug/Test methods
  static async testUserAuth(): Promise<{ user: any; isAuthenticated: boolean }> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    return {
      user: userData.user,
      isAuthenticated: !userError && !!userData.user
    };
  }

  static async testApplicationPermissions(teamRequestId: string): Promise<{
    canView: boolean;
    canInsert: boolean;
    error?: string;
  }> {
    try {
      // Test if user can view applications for this team request
      const { error: viewError } = await supabase
        .from('team_applications')
        .select('id')
        .eq('team_request_id', teamRequestId)
        .limit(1);

      // Test if user can insert (by attempting a dry run - we'll rollback)
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        return { canView: false, canInsert: false, error: 'Not authenticated' };
      }

      // For insert test, we'll check the RLS policy indirectly
      const { data: teamRequest } = await supabase
        .from('team_requests')
        .select('id, publisher_id')
        .eq('id', teamRequestId)
        .single();

      return {
        canView: !viewError,
        canInsert: !!teamRequest, // If we can see the team request, we should be able to apply
        error: viewError?.message
      };
    } catch (error: any) {
      return { canView: false, canInsert: false, error: error.message };
    }
  }

  // Debug application visibility for a specific user
  static async debugApplicationVisibility(userId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .rpc('debug_team_application_visibility', { p_user_id: userId });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error in debug application visibility:', error);
      throw error;
    }
  }

  // Check team formation eligibility
  static async checkTeamFormationEligibility(requestId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .rpc('check_team_formation_eligibility', { p_request_id: requestId });

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('Error checking team formation eligibility:', error);
      throw error;
    }
  }

  // Manual trigger for team formation (for debugging)
  static async manualCheckTeamFormation(requestId: string): Promise<void> {
    await this.checkAndAutoFormTeam(requestId);
  }

  // Debug method specifically for team application issues
  static async debugTeamApplicationFlow(teamRequestId: string): Promise<any> {
    try {
      console.log('=== DEBUG TEAM APPLICATION FLOW ===');
      console.log('Team Request ID:', teamRequestId);

      // 1. Get team request details
      const { data: teamRequest, error: trError } = await supabase
        .from('team_requests')
        .select('*')
        .eq('id', teamRequestId)
        .single();

      if (trError) throw trError;
      console.log('Team Request:', teamRequest);

      // 2. Get ALL applications for this request
      const { data: allApplications, error: appsError } = await supabase
        .from('team_applications')
        .select(`
          *,
          applicant:users!team_applications_applicant_id_fkey(username, display_name)
        `)
        .eq('team_request_id', teamRequestId)
        .order('applied_at', { ascending: false });

      if (appsError) throw appsError;
      console.log('All Applications:', allApplications);

      // 3. Count by status
      const statusCounts = (allApplications || []).reduce((acc, app) => {
        acc[app.status] = (acc[app.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log('Status Counts:', statusCounts);

      // 4. Check team formation eligibility
      const eligibility = await this.checkTeamFormationEligibility(teamRequestId);
      console.log('Formation Eligibility:', eligibility);

      // 5. Check if team was already formed
      const { data: existingTeam, error: teamError } = await supabase
        .from('teams')
        .select(`
          *,
          team_members(
            id, user_id, role, is_active,
            user:users!team_members_user_id_fkey(username, display_name)
          )
        `)
        .eq('team_request_id', teamRequestId)
        .single();

      if (!teamError && existingTeam) {
        console.log('Existing Team Found:', existingTeam);
        console.log('Team Members:', existingTeam.team_members);
      } else {
        console.log('No team formed yet');
      }

      return {
        teamRequest,
        allApplications,
        statusCounts,
        eligibility,
        existingTeam: existingTeam || null
      };

    } catch (error) {
      console.error('Error in debug team application flow:', error);
      throw error;
    }
  }

  // Debug method to check team member count issues
  static async debugTeamMembers(teamId: string): Promise<any> {
    try {
      console.log('=== DEBUG TEAM MEMBERS ===');
      console.log('Team ID:', teamId);

      // 1. Get team details
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('*')
        .eq('id', teamId)
        .single();

      if (teamError) throw teamError;
      console.log('Team Details:', team);

      // 2. Get ALL team members (active and inactive)
      const { data: allMembers, error: membersError } = await supabase
        .from('team_members')
        .select(`
          *,
          user:users!team_members_user_id_fkey(username, display_name)
        `)
        .eq('team_id', teamId)
        .order('joined_at', { ascending: false });

      if (membersError) throw membersError;
      console.log('All Team Members:', allMembers);

      // 3. Count by status
      const memberCounts = (allMembers || []).reduce((acc, member) => {
        const status = member.is_active ? 'active' : 'inactive';
        acc[status] = (acc[status] || 0) + 1;
        acc.total = (acc.total || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log('Member Counts:', memberCounts);

      // 4. Get team request that created this team
      const { data: teamRequest, error: requestError } = await supabase
        .from('team_requests')
        .select('*')
        .eq('team_id', teamId)
        .single();

      if (!requestError && teamRequest) {
        console.log('Original Team Request:', teamRequest);
        console.log('Expected team size:', teamRequest.team_size);
        console.log('Actual active members:', memberCounts.active || 0);
      }

      return {
        team,
        allMembers,
        memberCounts,
        teamRequest: teamRequest || null
      };

    } catch (error) {
      console.error('Error in debug team members:', error);
      throw error;
    }
  }

  // Debug method to test available teams visibility
  static async debugAvailableTeams(userId: string): Promise<any> {
    try {
      console.log('=== DEBUG AVAILABLE TEAMS ===');
      console.log('User ID:', userId);

      // 1. Test basic team_requests query
      console.log('1. Testing basic team_requests query...');
      const { data: allRequests, error: allError } = await supabase
        .from('team_requests')
        .select('id, title, status, publisher_id')
        .order('created_at', { ascending: false });

      if (allError) {
        console.error('Error fetching all team requests:', allError);
      } else {
        console.log('All team requests:', allRequests?.length || 0);
        console.log('Team requests by status:',
          (allRequests || []).reduce((acc, req) => {
            acc[req.status] = (acc[req.status] || 0) + 1;
            return acc;
          }, {} as Record<string, number>)
        );
      }

      // 2. Test active team requests not by user
      console.log('2. Testing active team requests not by user...');
      const { data: activeRequests, error: activeError } = await supabase
        .from('team_requests')
        .select('id, title, publisher_id, status')
        .eq('status', 'active')
        .neq('publisher_id', userId);

      if (activeError) {
        console.error('Error fetching active team requests:', activeError);
      } else {
        console.log('Active team requests (not by user):', activeRequests?.length || 0);
        console.log('Active requests:', activeRequests);
      }

      // 3. Test user's applications
      console.log('3. Testing user applications...');
      const { data: userApps, error: appsError } = await supabase
        .from('team_applications')
        .select('team_request_id, status')
        .eq('applicant_id', userId);

      if (appsError) {
        console.error('Error fetching user applications:', appsError);
      } else {
        console.log('User applications:', userApps?.length || 0);
        console.log('Applications:', userApps);
      }

      // 4. Test the actual getAvailableTeamsForUser method
      console.log('4. Testing getAvailableTeamsForUser...');
      const availableTeams = await this.getAvailableTeamsForUser(userId);
      console.log('Available teams result:', availableTeams?.length || 0);

      return {
        allRequests: allRequests?.length || 0,
        activeRequests: activeRequests?.length || 0,
        userApplications: userApps?.length || 0,
        availableTeams: availableTeams?.length || 0,
        rawData: {
          allRequests,
          activeRequests,
          userApps,
          availableTeams
        }
      };

    } catch (error) {
      console.error('Error in debug available teams:', error);
      throw error;
    }
  }
}
